import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { IonContent, IonFooter, IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { ExternalFilterType } from '../../list-view.model';
import { ListViewCheckboxFilterComponent } from '../list-view-checkbox-filter.component';
import { CheckboxExternalFilterColumn, RangeExternalFilterColumn } from '../list-view-external-filtering.model';
import { ListViewExternalFilteringService } from '../list-view-external-filtering.service';
import { ListViewRangeFilterComponent } from '../list-view-range-filter.component';

export const LIST_VIEW_EXTERNAL_FILTERING_DIALOG_ID = 'list-view-external-filtering-dialog';

@Component({
  selector: 'dojo-list-view-external-filtering-dialog',
  templateUrl: './list-view-external-filtering-dialog.component.html',
  styleUrl: './list-view-external-filtering-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListViewCheckboxFilterComponent,
    ListViewRangeFilterComponent,
    ReactiveFormsModule,
    IonHeader,
    IconButtonComponent,
    IonToolbar,
    IonTitle,
    ButtonComponent,
    IconComponent,
    IonFooter,
    IonContent,
    TranslocoPipe,
  ],
  animations: [getAnimationAppearDisappearWithHeight()],
})
export class ListViewExternalFilteringDialogComponent {
  constructor(private readonly modalController: ModalController) {}

  readonly filteringService = input.required<ListViewExternalFilteringService>();

  readonly ExternalFilterType = ExternalFilterType;

  readonly hasTopicFilter = computed(() =>
    this.filteringService()
      .filterColumns()
      .some((col) => col.colId === 'topicLevel1'),
  );

  readonly state = connectState({
    hasFilters: toObservable(computed(() => this.filteringService().filterColumns())).pipe(
      switchMap((filterColumns) => {
        return combineLatest(
          filterColumns.map((filterColumn) => {
            if (filterColumn.filter === ExternalFilterType.Checkbox) {
              return filterColumn.formCtrl.valueChanges.pipe(
                startWith(filterColumn.formCtrl.value),
                map((value) => Array.isArray(value) && value.length > 0),
              );
            }
            if (filterColumn.filter === ExternalFilterType.Range) {
              return filterColumn.formCtrl.valueChanges.pipe(
                startWith(filterColumn.formCtrl.value),
                map((value) => value !== null),
              );
            }

            return of(false);
          }),
        );
      }),
      map((values) => values.some((value) => value)),
    ),
  });

  removeCheckboxValue(colId: string, value: string) {
    const filterColumns = this.filteringService().filterColumns();
    const filterColumn = filterColumns.find((column) => column.colId === colId);

    if (filterColumn) {
      const formCtrl = (filterColumn as CheckboxExternalFilterColumn).formCtrl;
      const valueArray = formCtrl.value as string[];
      formCtrl.setValue(valueArray.filter((val) => val !== value));
    }
  }

  removeRangeValue(colId: string) {
    const filterColumn = this.filteringService()
      .filterColumns()
      .find((column) => column.colId === colId);

    if (filterColumn) {
      (filterColumn as RangeExternalFilterColumn).formCtrl.setValue(null);
    }
  }

  resetFilter() {
    this.filteringService().resetFilter();
  }

  closeDialog() {
    this.modalController.dismiss(undefined, undefined, LIST_VIEW_EXTERNAL_FILTERING_DIALOG_ID);
  }

  confirm() {
    this.filteringService().applyFilter();
    this.modalController.dismiss(true, 'confirm', LIST_VIEW_EXTERNAL_FILTERING_DIALOG_ID);
  }
}
